import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { chain, groupBy } from "lodash";

import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Domain,
  ExpandLess,
  ExpandMore,
  PeopleAlt,
  Place,
  RestartAlt,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

import { ActionButtons, ButtonIcon, HelpBox, HelpText, MiniStatBox, SearchBox, TitleText } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { SiteMap } from "@/components/molecules";
import {
  useAudience,
  useAudienceDialogs,
  useAudiencePageState,
  useAudienceWorkflow,
  useSelectedSites,
  useServices,
  useSiteSelection,
  useSites,
} from "@/hooks";
import { ActionType, Site } from "@/types";
import {
  AudiencePageActionId,
  AudiencePageRules,
  abbreviateNumber,
  audiencePageActions,
  catchmentCategories,
  catchmentDayType, catchmentSiteType,
  createSiteDefinition,
  filterCatchment,
} from "@/utils";
import { SiteSkeleton } from "./SiteSkeleton";



const BUILD_ACTIONS: ActionType[] = ['catchment', 'build'];
const ACTION_ERRORS: AudiencePageRules["actionErrors"] = {
  SiteCatchmentError: ['catchment', 'build'],
  InsightError: ['insight'],
};
// Add 'share' once sharing site audiences is supported
const PAGE_ACTIONS: AudiencePageActionId[] = ['delete', 'insight', 'export'];

const SitePage: React.FC = () => {

  const { ui } = useServices();

  // Fetch Audience
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "site");

  // Prepare Site Data
  const {
    sites, loading: siteLoad, error: siteErr
  } = useSites();

  // Parse selected sites from the audience
  const selectedSites = useSelectedSites(sites);

  // Site Selection variables
  const [searchText, setSearchText] = useState<string>("");
  const [siteType, setSiteType] = useState<string>("ALL");
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [openRegions, setOpenRegions] = useState<Record<string, boolean>>({});

  const workflow = useAudienceWorkflow(audienceId, audience);
  const { action, isRunning } = workflow;
  const dialogs = useAudienceDialogs(audience);
  const siteSelection = useSiteSelection(audience, workflow);
  const selectAll = siteSelection.allSelected;

  // Editing isn't locked while catchments load, so sites can be added without waiting
  const rules = useMemo(() => ({
    readyToBuild: false,
    lockWhileBusy: false,
    buildActions: BUILD_ACTIONS,
    actionErrors: ACTION_ERRORS,
    extraErrors: { DataUnavailable: siteErr },
  }), [siteErr]);

  const pageState = useAudiencePageState({
    workflow,
    audience,
    isNew: audienceId === undefined,
    loading: audLoad || siteLoad,
    loadError: !!(audErr || siteErr),
    audienceError: audErr,
    definition: createSiteDefinition(audience),
    rules,
  });

  const catchmentLoading = isRunning('catchment');
  const sizeLoading = isRunning('build');

  const filteredSites = useMemo(() => {
    let searchSites: Site[] = sites;
    if (searchText !== '') {
      searchSites = searchSites.filter(site => (
        site.site_name.toLowerCase().includes(searchText.toLowerCase()))
        || (site.landlord && site.landlord.toLowerCase().includes(searchText.toLowerCase()))
        || (site.region && site.region.toLowerCase().includes(searchText.toLowerCase()))
      )
    }
    if (siteType !== 'ALL') {
      searchSites = searchSites.filter(x => x.site_type === siteType)
    }
    return chain(searchSites)
      .groupBy('region')
      .map((value, key) => ({region: key, sites: value}))
      .orderBy(['region'], ['asc'])
      .value();

  }, [sites, searchText, siteType]);

  const filteredCatchment = useMemo(() => {
    const catchment = audience?.site_config.catchment;
    const categories = audience?.site_config.categories;
    const dayType = audience?.site_config.day_type;
    if (!catchment || !categories || catchment.length === 0) return [];

    return filterCatchment(catchment, categories, dayType)

  }, [audience?.site_config.catchment, audience?.site_config.categories, audience?.site_config.day_type])

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const clearText = () =>
    setSearchText("");

  const toggleRegion = (region: string) => {
    setOpenRegions((prev) => ({
      ...prev,
      [region]: prev[region] === undefined ? false : !prev[region],
    }));
  };

  const pageActions = useMemo(
    () => audiencePageActions(pageState, workflow, dialogs, PAGE_ACTIONS),
    [pageState, workflow, dialogs]
  );


  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={isRunning('insight')}
      loadingText={action.message}
    >
      {pageState.isLoading ?
        <SiteSkeleton />
      :
      <Grid container spacing={2} sx={{ pb: 4 }}>
        {audience && sites && (
          <>

            {/* Header */}
            <Grid
              size={12}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="h6">
                {audience.audience_name || "New Site Selection"}
              </Typography>
              {selectedSites && audience.audience_size ? (
                <Stack direction="row" spacing={2}>
                  <MiniStatBox
                    name="Sites Selected"
                    value={selectAll ? sites.length : selectedSites.length}
                    icon={<Domain fontSize="small" color="secondary" />}
                  />
                  <MiniStatBox
                    name="Catchment Sectors"
                    value={filteredCatchment.length}
                    icon={<Place fontSize="small" color="secondary" />}
                  />
                  <MiniStatBox
                    name="Exposed Audience"
                    value={audience.audience_size[0].population_count | 0}
                    icon={<PeopleAlt fontSize="small" color="secondary" />}
                    valueFormatter={abbreviateNumber}
                    loading={sizeLoading}
                  />
                </Stack>
              ) : (
                sizeLoading && (
                  <Stack direction="row" spacing={2}>
                    <Skeleton variant="rounded" width={120} height={50} />
                    <Skeleton variant="rounded" width={120} height={50} />
                    <Skeleton variant="rounded" width={120} height={50} />
                  </Stack>
                )
              )}
            </Grid>

            <Grid size={12}>
              <TitleText
                text="Select your Sites"
                subtext={`
                Select the sites you want to profile.
                Each site has a Catchment calculated by combining vehicle visits using ANPR, purchase data from Open Banking and movement tracking from Mobile Devices.
                `}
                variant="body1"
                spacing={0.5}
              />
            </Grid>

            {/* Site Selection */}
            <Grid size={4}>
              <Stack spacing={1}>
                <Paper
                  sx={{
                    height: 544,
                    overflowY: "hidden",
                    display: "flex",
                    flexDirection: "column"
                  }}>

                  {/* Search Box */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "6px 6px 0px 0px",
                      borderBottom: 1,
                      borderColor: "divider",
                      flexShrink: 0
                    }}
                  >
                    <Stack spacing={1} direction="row" sx={{ pb: 2 }}>
                      <SearchBox
                        value={searchText}
                        onSearch={handleSearchTextChange}
                        fullWidth={true}
                        placeholder="Search for a site"
                      />
                      {searchText !== "" && (
                        <ButtonIcon
                          onClick={clearText}
                          color="info"
                          size={40}
                          Icon={RestartAlt}
                          tooltip="Clear Search Text"
                        />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="caption">
                        Filter
                      </Typography>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        {catchmentSiteType.map((option, idx) => {
                          const isSelected = siteType === option.name;
                          return (
                            <HelpBox title={option.description} key={idx}>
                              <Chip
                                key={option.name}
                                label={option.label}
                                size="small"
                                variant={isSelected ? "filled" : "outlined"}
                                color={isSelected ? "secondary" : "default"}
                                onClick={() => setSiteType(option.name)}
                                disabled={catchmentLoading}
                              />
                            </HelpBox>
                          )
                        })}
                      </Stack>
                    </Stack>
                  </Box>


                  {/* Selection Summary */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "6px 6px 0px 0px",
                      borderBottom: 1,
                      borderColor: "divider",
                      flexShrink: 0
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="secondary">
                          Selected {selectAll ? "ALL" : selectedSites.length}
                        </Typography>
                        {selectedSites.length > 0 && (
                          <HelpBox title="Clear all selected sites">
                            <IconButton
                              size="small"
                              onClick={siteSelection.clear}
                            >
                              <RestartAlt fontSize="small" />
                            </IconButton>
                          </HelpBox>
                        )}
                      </Stack>
                      {selectedSites.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => setExpanded((prev) => !prev)}
                        >
                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      )}
                    </Stack>
                    <Collapse in={isExpanded}>
                      <Box
                        sx={{
                          maxHeight: 96,
                          overflowY: "auto",
                          mt: 1,
                          mr: -2,
                          // mb: -2,
                          // pb: 2
                        }}
                      >
                        {selectedSites.map((site, idx) => (
                          <Chip
                            key={idx}
                            label={site.site_name}
                            size="small"
                            variant="filled"
                            color="secondary"
                            onDelete={() => siteSelection.remove(site)}
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Site List */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      minHeight: 0
                    }}
                  >
                    <List dense sx={{ p: 1 }}>
                      {searchText === '' && siteType === 'ALL' && (
                        <ListItemButton
                          selected={selectAll}
                          onClick={siteSelection.selectAll}
                          sx={{ height: '50px', borderRadius: '6px', py: 0, mb: 1 }}
                        >
                          <ListItemText primary="ALL SITES"/>
                        </ListItemButton>
                      )}
                      {filteredSites && filteredSites.map(({ region, sites }) => {
                        const isOpen = openRegions[region] ?? true;
                        const selectedSiteIds = new Set(selectedSites.map(x => x.site_id));
                        const regionSites = sites.map(x => x.site_id);
                        const allSelected = regionSites.every(x => selectedSiteIds.has(x));

                        return (
                          <React.Fragment key={region}>

                            <ListSubheader
                              component="div"
                              onClick={() => toggleRegion(region)}
                              sx={{
                                backgroundColor: 'background.paper',
                                backgroundImage: "var(--Paper-overlay)",
                                // lineHeight: '16px',
                                // letterSpacing: '0.15px',
                                mt: 2,
                                mb: 1,
                                py: 0.5,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box display='flex' flexDirection='row' alignItems='center'>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontFamily: 'poppins-semibold'
                                  }}
                                >
                                  {region} ({sites.length})
                                </Typography>
                                <Button
                                  size="small"
                                  variant="text"
                                  color="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    siteSelection.selectRegion(sites, allSelected);
                                  }}
                                  sx={{ fontSize: '10px', textTransform: 'none', mx: 1, minWidth: 'auto' }}
                                >
                                  {allSelected ? 'Deselect All' : 'Select All'}
                                </Button>
                              </Box>
                              <IconButton size="small">
                                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              </IconButton>
                            </ListSubheader>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box>
                                {sites.map((site) => (
                                  <ListItemButton
                                    key={site.site_id}
                                    selected={selectedSites.some(item => item.site_id === site.site_id)}
                                    onClick={() => siteSelection.toggle(site)}
                                    sx={{ borderRadius: '6px', py: 0, mb: 1 }}
                                  >
                                    <ListItemText
                                      primary={site.site_name}
                                      secondary={site.landlord}
                                      slotProps={{
                                        secondary: {
                                          fontSize: '12px'
                                        }
                                      }}
                                    />
                                  </ListItemButton>
                                ))}
                              </Box>
                            </Collapse>
                          </React.Fragment>
                        )

                      })}
                    </List>
                  </Box>
                </Paper>
              </Stack>
            </Grid>

            {/* Catchment Map */}
            <Grid size={8}>
              <SiteMap catchment={filteredCatchment} sites={selectedSites} selectAll={selectAll} loading={catchmentLoading}/>
            </Grid>

            <Grid size={12}>
              <TitleText
                text="Configure your Catchment"
                subtext={`
                Fine tune the catchment by selecting the type of visitors you want to profile and when they are likely to visit
                `}
                variant="body1"
                spacing={0.5}
              />
            </Grid>

            {/* Catchment Config */}
            <Grid>
              <Stack spacing={1.5} mr={2}>
                <HelpText
                  variant="caption"
                  text="Visitor Type (Select to include/exclude)"
                  maxWidth={400}
                  helpText={`
                  Postal Sectors in the Catchment have been classified into Visitor Types based on the frequency & volume 
                  of visits to each Site. 
                  `}
                />
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {catchmentCategories.map((category, idx) => {
                    const isSelected = audience.site_config.categories.includes(category.name);
                    const hexColor = ui.theme.palette.gradient[idx]
                    return (
                      <HelpBox title={category.description} key={idx}>
                        <Chip
                          key={category.name}
                          label={category.label}
                          size="small"
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            backgroundColor: isSelected ? hexColor : "transparent",
                            borderColor: isSelected ? "none" : hexColor,
                            '&:hover': {
                              backgroundColor: isSelected ? hexColor : `${hexColor}15`,
                              borderColor: hexColor,
                              opacity: 0.9,
                            },
                          }}
                          onClick={() => siteSelection.toggleCategory(category.name)}
                          disabled={catchmentLoading}
                        />
                      </HelpBox>
                    )
                  })}
                </Stack>
              </Stack>
            </Grid>

            {/* DOW Config */}
            <Grid>
              <Stack spacing={1.5}>
                <HelpText
                  variant="caption"
                  text="Day Type"
                  maxWidth={400}
                  helpText="Toggle between weekday or weekend journeys"
                />
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {catchmentDayType.map((option, idx) => {
                    const isSelected = audience.site_config.day_type === option.name;
                    return (
                      <HelpBox title={option.description} key={idx}>
                        <Chip
                          key={option.name}
                          label={option.label}
                          size="small"
                          variant={isSelected ? "filled" : "outlined"}
                          color={isSelected ? "secondary" : "default"}
                          onClick={() => siteSelection.selectDayType(option.name)}
                          disabled={catchmentLoading}
                        />
                      </HelpBox>
                    )
                  })}
                </Stack>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>
      }
    </PageContainer>
  );
};

export default SitePage;
